import {
  extractRuns, faceOf, key, keyOf, parseKey,
  type Coord, type CoordKey, type Letter, type RunReport, type Seat, type Tile,
} from '@nt2/engine';
import { audio } from '../audio/engine.js';
import { prefs, setPrefs } from '../state/prefs.js';
import { announce, clamp, el, on } from './dom.js';
import { sparkle } from './effects.js';

/**
 * What a drag on empty board does. `pan` moves the board (and a hold there
 * starts a freehand selection); `lasso` draws a freehand selection straight
 * away; `select` draws a rectangle.
 */
export type Tool = 'select' | 'pan' | 'lasso';

/** Where one dropped letter will go: onto a free square, or swapped with a letter. */
export type DropTarget =
  | { kind: 'place'; at: Coord }
  | { kind: 'swap'; at: Coord; tileId: string };

export interface BoardCallbacks {
  onPlace(tileId: string, to: Coord): void;
  onRecall(tileId: string): void;
  onMoveGroup(moves: Array<{ tileId: string; to: Coord }>): void;
  /** Exchange two of the player's letters, wherever they are. */
  onSwap(a: string, b: string): void;
  /**
   * A single letter is being dragged off the board, or has been let go there.
   * Returns true when something else (the rack) took the drop.
   */
  onDragOutside(tileId: string, clientX: number, clientY: number, released: boolean): boolean;
  /** Typing across or down changed. */
  onDirection(dir: 'across' | 'down'): void;
  onAssignBlank(tileId: string, letter: Letter | null): void;
  /** Ask the rack for the tile a typed letter should consume. */
  takeFromRack(letter: Letter): Tile | null;
  /** True when word colouring is permitted by the match's peek policy. */
  showStatus(): boolean;
  runStatus(): readonly RunReport[];
  /**
   * What the engine worked out each unassigned blank must stand for.
   *
   * Shown only when the player is already entitled to know whether their
   * words are real, because an inferred letter is itself the answer to that
   * question: a blank that has been given a letter is a blank in a word the
   * list accepts.
   */
  inferredBlanks(): ReadonlyMap<CoordKey, Letter>;
}

interface View { x: number; y: number; scale: number; }

/** Hold a tile this long to grab the whole word it sits in. */
/**
 * How long a hold on a letter takes to pick that letter out.
 *
 * Long enough not to fire on a drag that starts slowly, short enough that
 * gathering six letters does not feel like six separate waits.
 */
const LONG_PRESS_MS = 420;
/**
 * Hold empty space this long, without moving, to start selecting an area.
 *
 * A plain drag on empty space moves the board, because that is what dragging a
 * sheet of paper does and it is the thing people reach for constantly. Holding
 * still first is a deliberate change of intent, and a second is long enough
 * that it never happens by accident while panning.
 */
/**
 * How long a hold on empty board takes to start drawing round letters.
 *
 * It was a full second, and on Android that meant two buzzes: the system's own
 * long-press at about half a second — which drawing ignored — and then ours.
 * Now drawing starts at the system's long-press itself (the `contextmenu` event
 * it raises), and this is only the fallback for devices that raise none.
 */
const HOLD_TO_SELECT_MS = 500;
const DRAG_SLOP = 6;
/** Two taps on a tile within this long toggle it in and out of a selection. */
const DOUBLE_TAP_MS = 320;

/**
 * The playing surface.
 *
 * Responsibilities kept deliberately narrow: turn pointer, keyboard and wheel
 * input into engine actions, and draw whatever seat it is handed. It never
 * decides rules and never talks to the network — which is why the same
 * component renders a spectated opponent's grid in read-only mode.
 */
export class Board {
  readonly root: HTMLElement;
  #paper: HTMLElement;
  #layer: HTMLElement;
  #cursorEl: HTMLElement;
  #dropEl: HTMLElement;
  #marqueeEl: HTMLElement;
  /** The freehand outline being drawn, as an SVG path over the board. */
  #lassoEl: SVGSVGElement;
  #lassoPath: SVGPathElement;
  #emptyEl: HTMLElement;
  #home: HTMLElement;

  #seat: Seat | null = null;
  #readOnly = false;
  #tiles = new Map<string, HTMLElement>();
  #tagEls: HTMLElement[] = [];

  #view: View = { x: 0, y: 0, scale: 1 };
  #tool: Tool = 'pan';
  #selection = new Set<CoordKey>();
  #cursor: Coord = { x: 0, y: 0 };
  /** Which way tapped and typed letters go: a preference, so settings can change it. */
  get #dir(): 'across' | 'down' { return prefs().typeDir === 'down' ? 'down' : 'across'; }
  #cursorVisible = false;

  #cell = 46;
  #raf = 0;

  constructor(private cb: BoardCallbacks) {
    this.#paper = el('div', { class: 'board__paper' });
    this.#home = el('div', { class: 'board__home' });
    this.#layer = el('div', { class: 'board__layer' });
    this.#cursorEl = el('div', { class: 'board__cursor', 'data-dir': '→' });
    this.#cursorEl.hidden = true;
    this.#dropEl = el('div', { class: 'board__drop' });
    this.#dropEl.hidden = true;
    this.#marqueeEl = el('div', { class: 'board__marquee' });
    this.#marqueeEl.hidden = true;
    const ns = 'http://www.w3.org/2000/svg';
    this.#lassoEl = document.createElementNS(ns, 'svg');
    this.#lassoEl.setAttribute('class', 'board__lasso');
    this.#lassoEl.setAttribute('aria-hidden', 'true');
    this.#lassoPath = document.createElementNS(ns, 'path');
    this.#lassoEl.append(this.#lassoPath);
    this.#lassoEl.style.display = 'none';
    this.#emptyEl = el('div', { class: 'board__empty' },
      el('h2', { text: 'Your table is empty' }),
      el('p', { text: 'Drag a tile up from your rack, or just start typing.' }));

    this.root = el('div', {
      class: 'board', 'data-tool': 'pan', tabIndex: 0,
      role: 'application',
      'aria-label': 'Word grid. Drag to move the board, hold to select an area. ' +
        'Arrow keys move the cursor, type a letter to place a tile.',
    }, this.#paper, this.#emptyEl, this.#layer, this.#marqueeEl, this.#lassoEl);
    this.root.dataset.tool = 'pan';
    this.#layer.append(this.#home, this.#dropEl, this.#cursorEl);

    this.#readCell();
    this.#bindPointer();
    this.#bindKeyboard();
    this.#bindWheel();
    new ResizeObserver(() => { this.#readCell(); this.#applyView(); }).observe(this.root);
  }

  // -------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------

  setSeat(seat: Seat | null, readOnly = false): void {
    const changedSeat = this.#seat?.id !== seat?.id;
    this.#seat = seat;
    this.#readOnly = readOnly;
    if (changedSeat) { this.#selection.clear(); this.#view = { x: 0, y: 0, scale: this.#homeZoom }; }
    this.render();
    if (changedSeat) this.centreOnContent();
  }

  setTool(tool: Tool): void {
    this.#tool = tool;
    this.root.dataset.tool = tool;
  }
  get tool(): Tool { return this.#tool; }

  /** Rebuild tile elements, reusing nodes by tile id so animations survive. */
  render(): void {
    const seat = this.#seat;
    this.#emptyEl.hidden = !!seat && seat.grid.size > 0;
    const seen = new Set<string>();
    const statuses = this.cb.showStatus() ? this.#statusByCell() : null;
    this.#inferred = this.cb.showStatus() ? this.cb.inferredBlanks() : new Map();

    if (seat) {
      for (const [k, tile] of seat.grid) {
        const c = parseKey(k);
        seen.add(tile.id);
        let node = this.#tiles.get(tile.id);
        if (!node) {
          node = this.#makeTile(tile);
          this.#tiles.set(tile.id, node);
          this.#layer.append(node);
          /*
           * A letter arriving on the board is one the player has just put
           * there, so it must be solid from the first frame. It used to borrow
           * the rack's *dealing* animation — invisible and shrunken, fading in
           * over 420 ms — which on a drop read as the drag not having worked.
           * This is a brief settle into place instead, never hidden.
           */
          node.classList.add('tile--land');
          setTimeout(() => node?.classList.remove('tile--land'), 160);
        }
        this.#paintTile(node, tile, c);
        this.#positionTile(node, c);
        node.classList.toggle('tile--selected', this.#selection.has(k));
        const st = statuses?.get(k);
        node.classList.toggle('tile--in-valid', st === 'valid');
        node.classList.toggle('tile--in-invalid', st === 'invalid');
      }
    }
    for (const [id, node] of this.#tiles) {
      if (!seen.has(id)) { node.remove(); this.#tiles.delete(id); }
    }
    this.#renderRunTags();
    this.#applyView();
  }

  #makeTile(tile: Tile): HTMLElement {
    const node = el('div', {
      class: 'tile', 'data-tile': tile.id, role: 'img',
    });
    node.append(el('span', { class: 'tile__face' }));
    node.append(el('span', { class: 'tile__value' }));
    return node;
  }

  #paintTile(node: HTMLElement, tile: Tile, at?: Coord): void {
    const face = faceOf(tile);
    // A blank nobody has pinned down shows the letter the engine took it to
    // be, in its own style — so the player can read their own word without
    // ever having had to declare anything.
    const guess = tile.isBlank && !tile.assigned && at
      ? this.#inferred.get(keyOf(at)) ?? null
      : null;
    const label = face ?? guess ?? '?';
    const faceEl = node.firstElementChild as HTMLElement;
    if (faceEl.textContent !== label) faceEl.textContent = label;
    const val = node.lastElementChild as HTMLElement;
    const valText = tile.value > 0 ? String(tile.value) : '';
    if (val.textContent !== valText) val.textContent = valText;
    node.classList.toggle('tile--blank', tile.isBlank);
    node.classList.toggle('tile--unassigned', tile.isBlank && !tile.assigned && !guess);
    node.classList.toggle('tile--inferred', guess !== null);
    node.setAttribute('aria-label',
      tile.isBlank
        ? `blank tile${face ? `, playing as ${face}`
            : guess ? `, taken as ${guess}` : ', no letter chosen'}`
        : `letter ${label}, worth ${tile.value}`);
  }

  /** Refreshed once per paint, rather than per tile. */
  #inferred: ReadonlyMap<CoordKey, Letter> = new Map();

  #positionTile(node: HTMLElement, c: Coord): void {
    const pos = `translate3d(${c.x * this.#cell}px, ${c.y * this.#cell}px, 0)`;
    node.style.setProperty('--pos', pos);
    node.style.transform = pos;
  }

  /** Map each occupied cell to the worst status of the runs it belongs to. */
  #statusByCell(): Map<CoordKey, 'valid' | 'invalid'> {
    const out = new Map<CoordKey, 'valid' | 'invalid'>();
    for (const run of this.cb.runStatus()) {
      if (run.status !== 'valid' && run.status !== 'invalid') continue;
      for (const c of run.cells) {
        const k = keyOf(c);
        // Invalid wins: a tile in one good word and one bad word is a problem.
        if (run.status === 'invalid' || !out.has(k)) out.set(k, run.status);
      }
    }
    return out;
  }

  #renderRunTags(): void {
    for (const t of this.#tagEls) t.remove();
    this.#tagEls = [];
    if (!this.#seat || !prefs().showRunTags || !this.cb.showStatus()) return;
    for (const run of this.cb.runStatus()) {
      if (run.status !== 'invalid') continue;     // only flag the problems
      const tag = el('div', {
        class: 'runtag runtag--invalid', text: run.word,
      });
      tag.style.transform =
        `translate3d(${run.start.x * this.#cell}px, ${run.start.y * this.#cell - 3}px, 0) translateY(-100%)`;
      this.#layer.append(tag);
      this.#tagEls.push(tag);
    }
  }

  // -------------------------------------------------------------------
  // View: pan and zoom
  // -------------------------------------------------------------------

  #readCell(): void {
    const cs = getComputedStyle(this.root);
    const n = parseFloat(cs.getPropertyValue('--cell'));
    if (Number.isFinite(n) && n > 0) this.#cell = n;
    const z = parseFloat(cs.getPropertyValue('--board-zoom'));
    this.#homeZoom = Number.isFinite(z) && z > 0 ? z : 1;
  }

  /**
   * The zoom the board starts at, and returns to when re-centred.
   *
   * From `--board-zoom` in the stylesheet: 1 on a desktop, 1.5 on a phone,
   * where a player found the opening board too small to work on. Kept as a
   * zoom rather than a bigger cell size so that pinching out still goes as
   * far as it did.
   */
  #homeZoom = 1;

  #applyView(): void {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      const { x, y, scale } = this.#view;
      this.#layer.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      const step = this.#cell * scale;
      this.#paper.style.setProperty('--cell', `${step}px`);
      this.#paper.style.setProperty('--paper-x', `${x % step}px`);
      this.#paper.style.setProperty('--paper-y', `${y % step}px`);
      this.#home.style.transform = 'translate3d(0,0,0)';
      this.#updateCursorEl();
    });
  }

  /** Bring the player's words back into frame. */
  centreOnContent(): void {
    // Read afresh: the board is built before it is attached to the page, and
    // a detached element reports none of the stylesheet's custom properties —
    // so the phone's larger starting zoom was never seen.
    this.#readCell();
    const seat = this.#seat;
    const r = this.root.getBoundingClientRect();
    const home = this.#homeZoom;
    if (!seat || seat.grid.size === 0) {
      this.#view = {
        x: r.width / 2 - (this.#cell * home) / 2,
        y: r.height / 2 - (this.#cell * home) / 2,
        scale: home,
      };
      this.#applyView();
      return;
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const k of seat.grid.keys()) {
      const c = parseKey(k);
      minX = Math.min(minX, c.x); maxX = Math.max(maxX, c.x);
      minY = Math.min(minY, c.y); maxY = Math.max(maxY, c.y);
    }
    const w = (maxX - minX + 1) * this.#cell;
    const h = (maxY - minY + 1) * this.#cell;
    const pad = 48;
    const scale = clamp(Math.min((r.width - pad) / w, (r.height - pad) / h, home), 0.35, home);
    this.#view = {
      scale,
      x: r.width / 2 - (minX * this.#cell + w / 2) * scale,
      y: r.height / 2 - (minY * this.#cell + h / 2) * scale,
    };
    this.#applyView();
  }

  zoomBy(factor: number, at?: { x: number; y: number }): void {
    const r = this.root.getBoundingClientRect();
    const cx = at ? at.x - r.left : r.width / 2;
    const cy = at ? at.y - r.top : r.height / 2;
    const next = clamp(this.#view.scale * factor, 0.3, 2.2);
    const k = next / this.#view.scale;
    this.#view = {
      scale: next,
      x: cx - (cx - this.#view.x) * k,
      y: cy - (cy - this.#view.y) * k,
    };
    this.#applyView();
  }

  /** Screen point -> grid coordinate. */
  #toCell(clientX: number, clientY: number): Coord {
    const r = this.root.getBoundingClientRect();
    const lx = (clientX - r.left - this.#view.x) / this.#view.scale;
    const ly = (clientY - r.top - this.#view.y) / this.#view.scale;
    return { x: Math.floor(lx / this.#cell), y: Math.floor(ly / this.#cell) };
  }

  /** Screen point -> grid coordinate. Used by the rack when a drag lands. */
  cellAt(clientX: number, clientY: number): Coord {
    return this.#toCell(clientX, clientY);
  }

  /** The square the keyboard cursor is on, and whether it is showing. */
  get cursor(): Coord { return { ...this.#cursor }; }
  get cursorVisible(): boolean { return this.#cursorVisible; }

  /**
   * Drop a tile at the cursor and step on — the tap-to-type flow, which is how
   * the game is playable one-handed on a phone with no keyboard open.
   */
  placeAtCursor(tile: Tile): Coord | null {
    const seat = this.#seat;
    if (!seat || this.#readOnly) return null;
    let guard = 0;
    while (seat.grid.has(keyOf(this.#cursor)) && guard++ < 64) {
      this.#stepCursor(this.#dir === 'across' ? 1 : 0, this.#dir === 'down' ? 1 : 0);
    }
    const at = { ...this.#cursor };
    this.#cursorVisible = true;
    this.cb.onPlace(tile.id, at);
    this.#stepCursor(this.#dir === 'across' ? 1 : 0, this.#dir === 'down' ? 1 : 0);
    return at;
  }

  /** Redraw the cursor, whose arrow shows the direction. */
  refreshCursor(): void { this.#updateCursorEl(); }

  /** Put the cursor somewhere sensible when the player has not chosen. */
  ensureCursor(): void {
    if (this.#cursorVisible) return;
    const seat = this.#seat;
    let at: Coord = { x: 0, y: 0 };
    if (seat && seat.grid.size > 0) {
      // Just right of the rightmost tile on the topmost occupied row.
      let best: Coord | null = null;
      for (const k of seat.grid.keys()) {
        const c = parseKey(k);
        if (!best || c.y < best.y || (c.y === best.y && c.x > best.x)) best = c;
      }
      if (best) at = { x: best.x + 2, y: best.y };
    }
    this.#placeCursor(at);
  }

  // -------------------------------------------------------------------
  // Pointer input
  // -------------------------------------------------------------------

  #bindPointer(): void {
    /** Live pointers, so a second finger can turn a drag into a pinch. */
    const pointers = new Map<number, PointerEvent>();
    let mode: 'none' | 'tile' | 'marquee' | 'lasso' | 'pan' | 'pinch' = 'none';
    /** The freehand outline so far, in client coordinates. */
    let trail: Array<{ x: number; y: number }> = [];
    /** Mouse, pen or touch: a right-click raises the same event as a long press. */
    let downType = '';
    let startClient = { x: 0, y: 0 };
    let startCell: Coord = { x: 0, y: 0 };
    let dragIds: string[] = [];
    let dragOrigin = new Map<string, Coord>();
    let moved = false;
    let longPressTimer = 0;
    let holdTimer = 0;
    let pinchStart = { dist: 0, scale: 1, cx: 0, cy: 0 };
    let panStartView = { x: 0, y: 0 };
    let lastTap = { at: 0, tileId: '' };

    /**
     * A hold on empty board becomes a freehand selection. `buzz` is false when
     * the system has just vibrated for its own long-press: one buzz, not two.
     */
    const startDrawing = (buzz: boolean): void => {
      if (moved || mode !== 'pan' || pointers.size !== 1) return;
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = 0; }
      mode = 'lasso';
      trail = [startClient];
      this.root.classList.remove('is-panning');
      audio.play('select');
      if (buzz) audio.buzz(24);
      this.#drawLasso(trail);
    };

    const clearTimers = (): void => {
      if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = 0; }
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = 0; }
    };

    const distOf = (): number => {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return 0;
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };
    const midOf = (): { x: number; y: number } => {
      const [a, b] = [...pointers.values()];
      if (!a || !b) return { x: 0, y: 0 };
      return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
    };

    on(this.root, 'pointerdown', (ev) => {
      const e = ev as PointerEvent;
      pointers.set(e.pointerId, e);
      downType = e.pointerType;
      this.root.focus({ preventScroll: true });

      // Two fingers always mean pinch-zoom, whatever the first was doing.
      if (pointers.size === 2) {
        clearTimers();
        if (mode === 'tile') this.#cancelDrag(dragIds, dragOrigin);
        if (mode === 'marquee') this.#marqueeEl.hidden = true;
        if (mode === 'lasso') this.#hideLasso();
        mode = 'pinch';
        const mid = midOf();
        pinchStart = { dist: distOf(), scale: this.#view.scale, cx: mid.x, cy: mid.y };
        return;
      }
      if (pointers.size > 2) return;

      // A request, not a promise: a touchpad tap on a Mac can arrive with no
      // live pointer to capture, and a throw here abandoned the gesture half
      // begun. The window-level listeners below see it through regardless.
      try { this.root.setPointerCapture(e.pointerId); } catch { /* followed on the window */ }
      startClient = { x: e.clientX, y: e.clientY };
      startCell = this.#toCell(e.clientX, e.clientY);
      moved = false;

      const tileEl = (e.target as HTMLElement).closest<HTMLElement>('.tile');
      const onTile = tileEl?.dataset.tile;

      if (onTile && !this.#readOnly) {
        const k = keyOf(startCell);
        // Dragging any member of a selection drags the whole selection.
        const inSelection = this.#selection.has(k) && this.#selection.size > 1;
        dragIds = inSelection ? this.#selectedTileIds() : [onTile];
        dragOrigin = this.#originsOf(dragIds);
        mode = 'tile';
        this.#liftTiles(dragIds);
        audio.play('pickup');

        /*
         * Holding a letter picks out that one letter, and leaves whatever else
         * is already selected alone.
         *
         * It used to grab the whole word the letter sat in, which sounds
         * helpful and is not: a box selection is rectangular and a word is a
         * whole word, so between them there was no way to gather up an
         * arbitrary handful — four letters out of the middle of a block, say —
         * and slide them somewhere else. Holding each one in turn and then
         * dragging the set is exactly that, and it is the gesture a player
         * reaches for.
         *
         * Whole-word selection is still there on Alt-hold, for the times when
         * the word *is* what you meant.
         */
        clearTimers();
        longPressTimer = window.setTimeout(() => {
          if (moved || mode !== 'tile') return;
          this.#cancelDrag(dragIds, dragOrigin);
          mode = 'none';
          if (e.altKey) this.#selectRunsAt(startCell);
          else this.#toggleInSelection(startCell);
          audio.play('select');
          audio.buzz(18);
        }, LONG_PRESS_MS);
        return;
      }

      /*
       * Empty space. The board moves by default. Holding still first starts a
       * *freehand* selection: draw round exactly the letters you want, in any
       * shape, and let go. It used to start a rectangle, and a rectangle takes
       * whatever else happens to be inside it; the rectangle is still there as
       * its own tool for anyone who wants it, and so is freehand, for anyone
       * who would rather not wait for the hold.
       */
      if (this.#readOnly || e.button === 1) {
        mode = 'pan';
      } else if (this.#tool === 'select') {
        mode = 'marquee';
      } else if (this.#tool === 'lasso') {
        mode = 'lasso';
        trail = [startClient];
        this.#drawLasso(trail);
      } else {
        mode = 'pan';
        holdTimer = window.setTimeout(() => startDrawing(true), HOLD_TO_SELECT_MS);
      }
      if (mode === 'pan') {
        panStartView = { x: this.#view.x, y: this.#view.y };
        this.root.classList.add('is-panning');
      }
    });

    const onMove = (ev: Event): void => {
      const e = ev as PointerEvent;
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, e);

      if (mode === 'pinch') {
        const d = distOf();
        if (d > 0 && pinchStart.dist > 0) {
          const target = clamp(pinchStart.scale * (d / pinchStart.dist), 0.3, 2.2);
          const k = target / this.#view.scale;
          const r = this.root.getBoundingClientRect();
          const cx = pinchStart.cx - r.left;
          const cy = pinchStart.cy - r.top;
          this.#view = {
            scale: target,
            x: cx - (cx - this.#view.x) * k,
            y: cy - (cy - this.#view.y) * k,
          };
          this.#applyView();
        }
        return;
      }

      const dx = e.clientX - startClient.x;
      const dy = e.clientY - startClient.y;
      if (!moved && Math.hypot(dx, dy) > DRAG_SLOP) {
        moved = true;
        // Motion means this was a drag, not a hold: stop waiting to select.
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = 0; }
        if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = 0; }
      }

      if (mode === 'tile' && moved) {
        this.#dragTo(dragIds, dragOrigin, dx, dy, e.clientX, e.clientY);
      } else if (mode === 'pan' && moved) {
        this.#view = { ...this.#view, x: panStartView.x + dx, y: panStartView.y + dy };
        this.#applyView();
      } else if (mode === 'marquee') {
        this.#drawMarquee(startClient, { x: e.clientX, y: e.clientY });
      } else if (mode === 'lasso') {
        const last = trail[trail.length - 1]!;
        // Thin the trail: a point every few pixels is plenty for the outline
        // and keeps the containment test cheap on a long, slow loop.
        if (Math.hypot(e.clientX - last.x, e.clientY - last.y) >= 3) {
          trail.push({ x: e.clientX, y: e.clientY });
          this.#drawLasso(trail);
        }
      }
    };
    on(this.root, 'pointermove', onMove);

    const finish = (ev: Event): void => {
      const e = ev as PointerEvent;
      pointers.delete(e.pointerId);
      clearTimers();
      this.root.classList.remove('is-panning');
      try { this.root.releasePointerCapture(e.pointerId); } catch { /* already gone */ }

      if (mode === 'pinch') { if (pointers.size === 0) mode = 'none'; return; }

      if (mode === 'tile') {
        if (moved) this.#dropTiles(dragIds, dragOrigin, e.clientX, e.clientY, startClient);
        else this.#tapTile(dragIds[0] as string, startCell, e, lastTap);
        this.#dropEl.hidden = true;
        this.#unliftTiles(dragIds);
        lastTap = { at: moved ? 0 : Date.now(), tileId: moved ? '' : (dragIds[0] ?? '') };
      } else if (mode === 'marquee') {
        this.#commitMarquee(startClient, { x: e.clientX, y: e.clientY }, e.shiftKey);
        this.#marqueeEl.hidden = true;
      } else if (mode === 'lasso' && !moved && this.#tool === 'lasso') {
        // A tap with the freehand tool is still a tap: it puts a selection down.
        this.#hideLasso();
        trail = [];
        if (this.#selection.size) this.#setSelection([]);
        else this.#tapSquare(startCell);
      } else if (mode === 'lasso') {
        this.#commitLasso(trail, e.shiftKey);
        this.#hideLasso();
        trail = [];
      } else if (mode === 'pan' && !moved) {
        // A tap on bare board is how you put a selection down again.
        if (this.#selection.size) this.#setSelection([]);
        else this.#tapSquare(startCell);
        lastTap = { at: 0, tileId: '' };
      }
      mode = 'none';
      dragIds = [];
    };
    on(this.root, 'pointerup', finish);
    const abandon = (ev: Event): void => {
      // An interrupted gesture is cleanup only: it must not place, move,
      // select or move the cursor.
      const e = ev as PointerEvent;
      pointers.delete(e.pointerId);
      clearTimers();
      this.root.classList.remove('is-panning');
      try { this.root.releasePointerCapture(e.pointerId); } catch { /* gone */ }
      if (mode === 'tile') {
        this.#cancelDrag(dragIds, dragOrigin);
        this.#unliftTiles(dragIds);
        // Nowhere: clears anything the rack was showing for this drag.
        if (dragIds.length === 1) this.cb.onDragOutside(dragIds[0]!, NaN, NaN, false);
      }
      this.#marqueeEl.hidden = true;
      this.#hideLasso();
      trail = [];
      this.#dropEl.hidden = true;
      mode = 'none';
      dragIds = [];
      lastTap = { at: 0, tileId: '' };
    };
    on(this.root, 'pointercancel', abandon);

    /*
     * The same gesture, followed past the board's edge.
     *
     * With capture held, every event comes to the board and these do nothing.
     * Without it — never taken, or lost — the rest of the gesture lands on
     * whatever is under the pointer, and a lifted letter was left hanging
     * off the grid. These see it through: moves outside the board still move
     * it, and a release anywhere finishes it.
     */
    const outside = (e: Event): boolean =>
      !(e.target instanceof Node && this.root.contains(e.target));
    window.addEventListener('pointermove', (e) => {
      if (outside(e) && pointers.has((e as PointerEvent).pointerId)) onMove(e);
    });
    window.addEventListener('pointerup', (e) => {
      if (outside(e) && pointers.has((e as PointerEvent).pointerId)) finish(e);
    });
    window.addEventListener('pointercancel', (e) => {
      if (outside(e) && pointers.has((e as PointerEvent).pointerId)) abandon(e);
    });
    // Focus leaving mid-gesture: its end will never be reported, so undo it.
    window.addEventListener('blur', () => {
      for (const id of [...pointers.keys()]) {
        abandon(new PointerEvent('pointercancel', { pointerId: id }));
      }
    });

    // Long-press context menus fight with holding a tile.
    // The system's long-press. Its menu fights with holding a tile, so it is
    // always suppressed — but on empty board it is also the moment to start
    // drawing, in step with the buzz the system has already given.
    on(this.root, 'contextmenu', (e) => {
      e.preventDefault();
      if (this.#tool === 'pan' && !this.#readOnly && downType !== 'mouse') startDrawing(false);
    });
  }

  #selectedTileIds(): string[] {
    const seat = this.#seat;
    if (!seat) return [];
    const ids: string[] = [];
    for (const k of this.#selection) {
      const t = seat.grid.get(k);
      if (t) ids.push(t.id);
    }
    return ids;
  }

  #originsOf(ids: string[]): Map<string, Coord> {
    const out = new Map<string, Coord>();
    const seat = this.#seat;
    if (!seat) return out;
    for (const [k, t] of seat.grid) if (ids.includes(t.id)) out.set(t.id, parseKey(k));
    return out;
  }

  #liftTiles(ids: string[]): void {
    for (const id of ids) this.#tiles.get(id)?.classList.add('tile--lifted');
  }
  #unliftTiles(ids: string[]): void {
    for (const id of ids) this.#tiles.get(id)?.classList.remove('tile--lifted');
  }

  #dragTo(
    ids: string[], origin: Map<string, Coord>,
    dx: number, dy: number, clientX: number, clientY: number,
  ): void {
    const s = this.#view.scale;
    for (const id of ids) {
      const node = this.#tiles.get(id);
      const from = origin.get(id);
      if (!node || !from) continue;
      node.style.transform =
        `translate3d(${from.x * this.#cell + dx / s}px, ${from.y * this.#cell + dy / s}px, 0)`;
    }
    // Preview where the anchor tile will land, and whether it can.
    const anchor = ids[0];
    const from = anchor ? origin.get(anchor) : undefined;
    if (!from || !anchor) return;
    if (ids.length === 1) {
      // Over the rack: the rack shows where it would go, the board nothing.
      if (this.cb.onDragOutside(anchor, clientX, clientY, false)) { this.aim(null); return; }
      const step = this.#view.scale * this.#cell;
      this.#showDrop(this.resolveDrop({ x: from.x + dx / step, y: from.y + dy / step }, anchor), false);
      return;
    }
    const target = this.#snapTarget(from, dx, dy);
    const blocked = !this.#groupFits(ids, origin, target.dx, target.dy);
    this.#showDrop({ kind: 'place', at: { x: from.x + target.dx, y: from.y + target.dy } }, false);
    this.#dropEl.classList.toggle('board__drop--blocked', blocked);
  }

  /**
   * Where one letter goes if it is let go at `pos`.
   *
   * `pos` is where the letter's top-left corner would be, in squares, not
   * rounded: the nearest square is the obvious answer. When that square
   * already has a letter in it, the drop only counts as a swap if it is well
   * inside that letter — within the middle (1 − guard) of it. Anything else
   * was almost certainly meant for the free square next to it, so it goes to
   * whichever free square is nearest. In a race, a letter dropped a little
   * short of the gap beside a word is far more common than a deliberate swap.
   */
  resolveDrop(pos: { x: number; y: number }, movingId: string): DropTarget | null {
    const seat = this.#seat;
    if (!seat) return null;
    const at = { x: Math.round(pos.x), y: Math.round(pos.y) };
    const sitting = seat.grid.get(keyOf(at));
    if (!sitting || sitting.id === movingId) return { kind: 'place', at };
    const guard = clamp(prefs().swapGuard, 0, 1);
    const half = (1 - guard) / 2;
    if (guard < 1 && Math.abs(pos.x - at.x) <= half && Math.abs(pos.y - at.y) <= half) {
      return { kind: 'swap', at, tileId: sitting.id };
    }
    let best: Coord | null = null;
    let bestD = Infinity;
    for (let y = at.y - 2; y <= at.y + 2; y++) {
      for (let x = at.x - 2; x <= at.x + 2; x++) {
        const t = seat.grid.get(key(x, y));
        if (t && t.id !== movingId) continue;
        const d = Math.hypot(pos.x - x, pos.y - y);
        if (d < bestD) { bestD = d; best = { x, y }; }
      }
    }
    return best ? { kind: 'place', at: best } : null;
  }

  /**
   * Show where a letter being dragged from the rack will land. `null` hides it.
   * Green and pulsing for a free square; amber for a swap.
   */
  aim(target: DropTarget | null): void {
    if (!target) { this.#dropEl.hidden = true; return; }
    this.#showDrop(target, true);
  }

  #showDrop(target: DropTarget | null, fromRack: boolean): void {
    if (!target) { this.#dropEl.hidden = true; return; }
    this.#dropEl.hidden = false;
    this.#dropEl.classList.remove('board__drop--blocked');
    this.#dropEl.classList.toggle('board__drop--aim', fromRack && target.kind === 'place');
    this.#dropEl.classList.toggle('board__drop--swap', target.kind === 'swap');
    this.#dropEl.style.transform =
      `translate3d(${target.at.x * this.#cell}px, ${target.at.y * this.#cell}px, 0)`;
  }

  /** How big one square is on screen right now, zoom included. */
  get squareSize(): number { return this.#cell * this.#view.scale; }

  /**
   * A point on the screen as a letter's top-left corner, in squares, not
   * rounded — the letter being centred on the point.
   */
  pointToCells(clientX: number, clientY: number): { x: number; y: number } {
    const r = this.root.getBoundingClientRect();
    const lx = (clientX - r.left - this.#view.x) / this.#view.scale;
    const ly = (clientY - r.top - this.#view.y) / this.#view.scale;
    return { x: lx / this.#cell - 0.5, y: ly / this.#cell - 0.5 };
  }

  #snapTarget(from: Coord, dx: number, dy: number): { dx: number; dy: number } {
    const s = this.#view.scale * this.#cell;
    return { dx: Math.round(dx / s), dy: Math.round(dy / s) };
  }

  /** Would the whole group land on free squares (ignoring squares it vacates)? */
  #groupFits(ids: string[], origin: Map<string, Coord>, ddx: number, ddy: number): boolean {
    const seat = this.#seat;
    if (!seat) return false;
    const moving = new Set(ids);
    const targets = new Set<CoordKey>();
    for (const id of ids) {
      const from = origin.get(id);
      if (!from) continue;
      const k = key(from.x + ddx, from.y + ddy);
      if (targets.has(k)) return false;
      targets.add(k);
      const sitting = seat.grid.get(k);
      if (sitting && !moving.has(sitting.id)) return false;
    }
    return true;
  }

  #cancelDrag(ids: string[], origin: Map<string, Coord>): void {
    for (const id of ids) {
      const node = this.#tiles.get(id);
      const from = origin.get(id);
      if (node && from) this.#positionTile(node, from);
      node?.classList.remove('tile--lifted');
    }
    this.#dropEl.hidden = true;
  }

  #dropTiles(
    ids: string[], origin: Map<string, Coord>,
    clientX: number, clientY: number, startClient: { x: number; y: number },
  ): void {
    const anchor = ids[0];
    const from = anchor ? origin.get(anchor) : undefined;
    if (!from || !anchor) return;
    // Let go over the rack: the rack decides where in it the letter goes.
    if (ids.length === 1 && this.cb.onDragOutside(anchor, clientX, clientY, true)) return;
    // Dropped below the board edge: send it home to the rack.
    const r = this.root.getBoundingClientRect();
    if (clientY > r.bottom - 4 && ids.length === 1) {
      this.cb.onRecall(anchor);
      audio.play('drop');
      return;
    }
    if (ids.length === 1) {
      const step = this.#view.scale * this.#cell;
      const target = this.resolveDrop({
        x: from.x + (clientX - startClient.x) / step,
        y: from.y + (clientY - startClient.y) / step,
      }, anchor);
      if (!target || (target.at.x === from.x && target.at.y === from.y)) {
        this.#cancelDrag(ids, origin);
        return;
      }
      if (this.#selection.size > 1) this.#setSelection([]);
      if (target.kind === 'swap') this.cb.onSwap(anchor, target.tileId);
      else this.cb.onPlace(anchor, target.at);
      audio.play('drop');
      audio.buzz(8);
      return;
    }
    const { dx, dy } = this.#snapTarget(
      from, clientX - startClient.x, clientY - startClient.y);
    if (dx === 0 && dy === 0) { this.#cancelDrag(ids, origin); return; }
    if (!this.#groupFits(ids, origin, dx, dy)) {
      this.#cancelDrag(ids, origin);
      for (const id of ids) {
        const node = this.#tiles.get(id);
        node?.classList.add('tile--shake');
        setTimeout(() => node?.classList.remove('tile--shake'), 400);
      }
      audio.play('invalid');
      return;
    }
    const moves = ids.map((id) => {
      const o = origin.get(id) as Coord;
      return { tileId: id, to: { x: o.x + dx, y: o.y + dy } };
    });
    // Re-key the selection so a moved block stays selected under the finger.
    if (this.#selection.size > 1) {
      this.#setSelection(moves.map((m) => keyOf(m.to)));
    }
    if (moves.length === 1) this.cb.onPlace(moves[0]!.tileId, moves[0]!.to);
    else this.cb.onMoveGroup(moves);
    audio.play('drop');
    audio.buzz(8);
  }

  /**
   * A tap on a tile, not a drag.
   *
   * Double-tapping adds a letter to the selection or takes it out again, which
   * is how you assemble an arbitrary group to drag. A single tap on a tile
   * that is *not* in the selection puts the selection down — so getting out of
   * a selection is always one tap, anywhere, and never a puzzle.
   */
  /**
   * Add one letter to the selection, or take it out again.
   *
   * The whole point is that it leaves the rest of the selection alone, so a
   * player can pick out a handful one at a time. Hold, double-tap and
   * shift-click all land here.
   */
  #toggleInSelection(at: Coord): void {
    const k = keyOf(at);
    const next = new Set(this.#selection);
    if (next.has(k)) next.delete(k); else next.add(k);
    this.#setSelection([...next]);
  }

  #tapTile(
    tileId: string, at: Coord, e: PointerEvent,
    lastTap: { at: number; tileId: string },
  ): void {
    const seat = this.#seat;
    if (!seat || this.#readOnly) return;
    const k = keyOf(at);
    const isDouble = lastTap.tileId === tileId &&
      Date.now() - lastTap.at < DOUBLE_TAP_MS;

    if (isDouble || e.shiftKey) {
      this.#toggleInSelection(at);
      audio.play('select');
      audio.buzz(10);
      return;
    }

    if (this.#selection.size > 0 && !this.#selection.has(k)) {
      this.#setSelection([]);
      return;
    }
    if (this.#selection.size > 0 && this.#selection.has(k)) return;

    const tile = seat.grid.get(k);
    if (tile?.isBlank) { this.promptBlank(tile.id); return; }
    this.#buildThrough(at, tileId);
  }

  /** The letter last tapped to build through, and which way that went. */
  #through: { tileId: string; dir: 'across' | 'down'; cursor: Coord } | null = null;

  /**
   * A tap on a letter aims at the first free square past it, heading the way
   * the letter is free.
   *
   * A letter in an across word can only grow a down word, and the other way
   * round, so that is the way to go, and the cursor lands on the square just
   * beyond it, skipping any letters already there. A letter on its own, or one
   * already crossed both ways, keeps the current direction. Tapping the same
   * letter again, before anything else moves the cursor, takes the other way.
   */
  #buildThrough(at: Coord, tileId: string): void {
    const again = this.#through?.tileId === tileId
      && this.#cursorVisible
      && this.#cursor.x === this.#through.cursor.x && this.#cursor.y === this.#through.cursor.y;
    let dir: 'across' | 'down';
    if (again) {
      dir = this.#through!.dir === 'across' ? 'down' : 'across';
    } else if (prefs().autoDir) {
      const has = (x: number, y: number): boolean => !!this.#seat?.grid.has(key(x, y));
      const inAcross = has(at.x - 1, at.y) || has(at.x + 1, at.y);
      const inDown = has(at.x, at.y - 1) || has(at.x, at.y + 1);
      dir = inAcross && !inDown ? 'down' : inDown && !inAcross ? 'across' : this.#dir;
    } else {
      dir = this.#dir;
    }
    this.setDirection(dir);
    const dx = dir === 'across' ? 1 : 0, dy = dir === 'down' ? 1 : 0;
    let next = { x: at.x + dx, y: at.y + dy };
    let guard = 0;
    while (this.#seat?.grid.has(keyOf(next)) && guard++ < 64) next = { x: next.x + dx, y: next.y + dy };
    this.#placeCursor(next);
    this.#keepCursorInView();
    this.#through = { tileId, dir, cursor: next };
    audio.play('select');
  }

  #drawMarquee(a: { x: number; y: number }, b: { x: number; y: number }): void {
    const r = this.root.getBoundingClientRect();
    const x0 = Math.min(a.x, b.x) - r.left, x1 = Math.max(a.x, b.x) - r.left;
    const y0 = Math.min(a.y, b.y) - r.top, y1 = Math.max(a.y, b.y) - r.top;
    this.#marqueeEl.hidden = false;
    Object.assign(this.#marqueeEl.style, {
      left: `${x0}px`, top: `${y0}px`, width: `${x1 - x0}px`, height: `${y1 - y0}px`,
    });
  }

  #commitMarquee(
    a: { x: number; y: number }, b: { x: number; y: number }, additive: boolean,
  ): void {
    const seat = this.#seat;
    if (!seat) return;
    const c1 = this.#toCell(Math.min(a.x, b.x), Math.min(a.y, b.y));
    const c2 = this.#toCell(Math.max(a.x, b.x), Math.max(a.y, b.y));
    const picked: CoordKey[] = [];
    for (const k of seat.grid.keys()) {
      const c = parseKey(k);
      if (c.x >= c1.x && c.x <= c2.x && c.y >= c1.y && c.y <= c2.y) picked.push(k);
    }
    this.#setSelection(additive ? [...this.#selection, ...picked] : picked);
    if (picked.length) { audio.play('select'); announce(`${picked.length} tiles selected`); }
  }

  #drawLasso(points: ReadonlyArray<{ x: number; y: number }>): void {
    const r = this.root.getBoundingClientRect();
    const d = points.map((p, i) => `${i ? 'L' : 'M'}${(p.x - r.left).toFixed(1)} ${(p.y - r.top).toFixed(1)}`)
      .join(' ');
    // Closed as it is drawn, so the player can see what letting go will take.
    this.#lassoPath.setAttribute('d', points.length > 1 ? `${d} Z` : d);
    this.#lassoEl.style.display = '';
  }

  #hideLasso(): void {
    this.#lassoEl.style.display = 'none';
    this.#lassoPath.setAttribute('d', '');
  }

  /**
   * Select the letters inside a freehand outline.
   *
   * A letter counts when the centre of its square is inside — the natural
   * reading of "I drew round it" — tested by ray casting against the outline
   * closed back to its start. An outline too small to enclose anything is a
   * slip, not an instruction, and changes nothing.
   */
  #commitLasso(points: ReadonlyArray<{ x: number; y: number }>, additive: boolean): void {
    const seat = this.#seat;
    if (!seat || points.length < 3) return;
    const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
    if (Math.max(...xs) - Math.min(...xs) < 8 && Math.max(...ys) - Math.min(...ys) < 8) return;
    const r = this.root.getBoundingClientRect();
    const { x: vx, y: vy, scale } = this.#view;
    const step = this.#cell * scale;
    const picked: CoordKey[] = [];
    for (const k of seat.grid.keys()) {
      const c = parseKey(k);
      const px = r.left + vx + (c.x + 0.5) * step;
      const py = r.top + vy + (c.y + 0.5) * step;
      if (insidePolygon(px, py, points)) picked.push(k);
    }
    this.#setSelection(additive ? [...this.#selection, ...picked] : picked);
    if (picked.length) {
      audio.play('select');
      announce(`${picked.length} tile${picked.length === 1 ? '' : 's'} selected`);
    }
  }

  /** Select every tile in the across and down words through a cell. */
  #selectRunsAt(at: Coord): void {
    const seat = this.#seat;
    if (!seat) return;
    const k = keyOf(at);
    const picked = new Set<CoordKey>();
    for (const run of extractRuns(seat.grid)) {
      if (!run.cells.some((c) => keyOf(c) === k)) continue;
      for (const c of run.cells) picked.add(keyOf(c));
    }
    if (picked.size === 0 && seat.grid.has(k)) picked.add(k);
    this.#setSelection([...picked]);
    announce(`${picked.size} tiles selected`);
  }

  #setSelection(keys: CoordKey[]): void {
    this.#selection = new Set(keys);
    this.render();
  }

  get selection(): ReadonlySet<CoordKey> { return this.#selection; }
  clearSelection(): void { this.#setSelection([]); }

  selectAll(): void {
    const seat = this.#seat;
    if (seat) this.#setSelection([...seat.grid.keys()]);
  }

  /** Return every selected tile to the rack. */
  recallSelection(): void {
    for (const id of this.#selectedTileIds()) this.cb.onRecall(id);
    this.clearSelection();
  }

  // -------------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------------

  /** Which way tapped and typed letters go. */
  get direction(): 'across' | 'down' { return this.#dir; }

  setDirection(dir: 'across' | 'down'): void {
    if (dir === this.#dir) return;
    setPrefs({ typeDir: dir });
    this.#updateCursorEl();
    announce(dir === 'across' ? 'Letters go across' : 'Letters go down');
    this.cb.onDirection(dir);
  }

  /**
   * A tap on an empty square aims the cursor there; a second tap on the same
   * square turns it, across to down and back — the way crossword apps do it.
   */
  #tapSquare(at: Coord): void {
    if (this.#cursorVisible && at.x === this.#cursor.x && at.y === this.#cursor.y) {
      this.setDirection(this.#dir === 'across' ? 'down' : 'across');
      audio.play('select');
      return;
    }
    this.#placeCursor(at);
    this.#guessDirection(at);
  }

  /**
   * Choose the direction from where the tap was, so it rarely needs setting.
   *
   * The square under a letter can only carry on a down word, and the square
   * after a letter an across one. With letters on both sides the tap says
   * nothing, and with none it says nothing either: the direction stays put.
   */
  #guessDirection(at: Coord): void {
    const seat = this.#seat;
    if (!seat || !prefs().autoDir) return;
    const above = seat.grid.has(key(at.x, at.y - 1));
    const left = seat.grid.has(key(at.x - 1, at.y));
    if (above && !left) this.setDirection('down');
    else if (left && !above) this.setDirection('across');
  }

  #placeCursor(at: Coord): void {
    this.#cursor = at;
    this.#cursorVisible = true;
    this.#updateCursorEl();
  }

  #updateCursorEl(): void {
    this.#cursorEl.hidden = !this.#cursorVisible || this.#readOnly;
    this.#cursorEl.style.transform =
      `translate3d(${this.#cursor.x * this.#cell}px, ${this.#cursor.y * this.#cell}px, 0)`;
    this.#cursorEl.dataset.dir = this.#dir === 'across' ? '→' : '↓';
  }

  #stepCursor(dx: number, dy: number): void {
    this.#cursor = { x: this.#cursor.x + dx, y: this.#cursor.y + dy };
    this.#cursorVisible = true;
    this.#updateCursorEl();
    this.#keepCursorInView();
  }

  /** Nudge the view when the cursor walks off the edge. */
  #keepCursorInView(): void {
    const r = this.root.getBoundingClientRect();
    const s = this.#view.scale;
    const sx = this.#cursor.x * this.#cell * s + this.#view.x;
    const sy = this.#cursor.y * this.#cell * s + this.#view.y;
    const size = this.#cell * s;
    const pad = size;
    let { x, y } = this.#view;
    if (sx < pad) x += pad - sx;
    if (sy < pad) y += pad - sy;
    if (sx + size > r.width - pad) x -= sx + size - (r.width - pad);
    if (sy + size > r.height - pad) y -= sy + size - (r.height - pad);
    if (x !== this.#view.x || y !== this.#view.y) {
      this.#view = { ...this.#view, x, y };
      this.#applyView();
    }
  }

  #bindKeyboard(): void {
    on(this.root, 'keydown', (ev) => {
      const e = ev as KeyboardEvent;
      if (this.#readOnly) return;
      const mod = e.ctrlKey || e.metaKey;
      /**
       * Anything the board consumes stops here.
       *
       * The board holds focus for almost the whole match, so a global
       * single-letter shortcut would fire every time that letter was typed
       * onto the grid — pressing C to spell CAT would also open the word
       * checker. Swallowing handled keys keeps the two layers from fighting,
       * and is why the app-level shortcuts are digits and punctuation.
       */
      const consume = (): void => { e.preventDefault(); e.stopPropagation(); };

      // Nudge a selection with the modifier held; otherwise walk the cursor.
      if (e.key.startsWith('Arrow')) {
        const dx = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        const dy = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
        consume();
        if (mod && this.#selection.size) this.#nudgeSelection(dx, dy);
        else this.#stepCursor(dx, dy);
        return;
      }

      if (e.key === ' ' || e.key === 'Tab') {
        consume();
        this.setDirection(this.#dir === 'across' ? 'down' : 'across');
        return;
      }

      if (e.key === 'Escape') {
        consume();
        this.clearSelection();
        this.#cursorVisible = false;
        this.#updateCursorEl();
        return;
      }

      if (mod && e.key.toLowerCase() === 'a') { consume(); this.selectAll(); return; }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        consume();
        if (this.#selection.size) { this.recallSelection(); return; }
        // Backspace steps back then lifts, like a text field.
        const back = e.key === 'Backspace';
        if (back) this.#stepCursor(this.#dir === 'across' ? -1 : 0, this.#dir === 'down' ? -1 : 0);
        const t = this.#seat?.grid.get(keyOf(this.#cursor));
        if (t) { this.cb.onRecall(t.id); audio.play('pickup'); }
        return;
      }

      // Plain letters place tiles. A modifier means the player is reaching for
      // a browser or app shortcut, so those pass through untouched.
      if (!mod && !e.altKey && /^[a-zA-Z]$/.test(e.key)) {
        consume();
        this.#typeLetter(e.key.toUpperCase() as Letter);
        return;
      }
    });
  }

  #typeLetter(letter: Letter): void {
    const seat = this.#seat;
    if (!seat) return;
    this.#cursorVisible = true;
    // If the cursor sits on a tile already, step past it rather than refusing:
    // typing a word over existing letters should flow, not stall.
    let guard = 0;
    while (seat.grid.has(keyOf(this.#cursor)) && guard++ < 64) {
      const existing = seat.grid.get(keyOf(this.#cursor));
      if (existing && faceOf(existing) === letter) {
        this.#stepCursor(this.#dir === 'across' ? 1 : 0, this.#dir === 'down' ? 1 : 0);
        return;                                  // the letter is already there
      }
      this.#stepCursor(this.#dir === 'across' ? 1 : 0, this.#dir === 'down' ? 1 : 0);
    }
    const tile = this.cb.takeFromRack(letter);
    if (!tile) {
      audio.play('invalid');
      announce(`No ${letter} in your rack`);
      this.#cursorEl.animate(
        [{ transform: this.#cursorEl.style.transform + ' translateX(-3px)' },
         { transform: this.#cursorEl.style.transform + ' translateX(3px)' },
         { transform: this.#cursorEl.style.transform }],
        { duration: 180 });
      return;
    }
    const at = { ...this.#cursor };
    this.cb.onPlace(tile.id, at);
    if (tile.isBlank) this.cb.onAssignBlank(tile.id, letter);
    audio.play('drop');
    this.#stepCursor(this.#dir === 'across' ? 1 : 0, this.#dir === 'down' ? 1 : 0);
  }

  #nudgeSelection(dx: number, dy: number): void {
    const ids = this.#selectedTileIds();
    if (!ids.length) return;
    const origin = this.#originsOf(ids);
    if (!this.#groupFits(ids, origin, dx, dy)) { audio.play('invalid'); return; }
    const moves = ids.map((id) => {
      const o = origin.get(id) as Coord;
      return { tileId: id, to: { x: o.x + dx, y: o.y + dy } };
    });
    this.#setSelection(moves.map((m) => keyOf(m.to)));
    this.cb.onMoveGroup(moves);
    audio.play('drop', { throttleMs: 60 });
  }

  #bindWheel(): void {
    // Scrolling zooms, the way it does on a map: dragging already pans, so
    // wheel-to-pan was the redundant one, and reaching for the scroll wheel to
    // get back out of a zoom is the obvious instinct.
    on(this.root, 'wheel', (ev) => {
      const e = ev as WheelEvent;
      e.preventDefault();
      if (e.shiftKey) {
        this.#view = { ...this.#view, x: this.#view.x - e.deltaY - e.deltaX };
        this.#applyView();
        return;
      }
      const step = Math.exp(-e.deltaY / 380);
      this.zoomBy(clamp(step, 0.6, 1.6), { x: e.clientX, y: e.clientY });
    }, { passive: false });

    /*
     * Safari raises its own pinch gestures alongside pointer events. On the
     * board that would zoom the page on top of the board's own zoom, so they
     * are refused here — and only here, so a pinch anywhere else still works
     * as the escape hatch from an accidental page zoom.
     */
    for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
      this.root.addEventListener(type, (e) => e.preventDefault(), { passive: false });
    }
  }

  // -------------------------------------------------------------------

  /** Called by the app when a blank needs a letter chosen. */
  promptBlank(tileId: string): void {
    this.onBlankRequested?.(tileId);
  }
  onBlankRequested: ((tileId: string) => void) | null = null;

  /** Celebrate at a cell, used when a word completes. */
  celebrateCell(at: Coord, colour: string): void {
    const r = this.root.getBoundingClientRect();
    const s = this.#view.scale;
    sparkle(
      at.x * this.#cell * s + this.#view.x + (this.#cell * s) / 2,
      at.y * this.#cell * s + this.#view.y + (this.#cell * s) / 2,
      colour, 10);
    void r;
  }

  /** Where the take-two ring should burst from: the centre of the grid. */
  contentCentre(): { x: number; y: number } {
    const r = this.root.getBoundingClientRect();
    return { x: r.width / 2, y: r.height / 2 };
  }
}

/** Even–odd ray casting: is the point inside the closed outline? */
function insidePolygon(
  x: number, y: number, poly: ReadonlyArray<{ x: number; y: number }>,
): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!, b = poly[j]!;
    if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}
